import { Currency, CurrencyNames } from "@redotech/money/iso4217/currencies";
import { useTranslation } from "@redotech/redo-i18n";
import { memo, useMemo, useState } from "react";
import { TextRedoListItem } from "../list/redo-list";
import {
  RedoDropdownInputSize,
  RedoSingleSelectDropdownInput,
} from "./redo-single-select-dropdown-input";

const CURRENCY_OPTIONS: TextRedoListItem<Currency>[] = Object.values(Currency)
  .map((currency) => ({
    type: "text" as const,
    id: currency,
    text: CurrencyNames[currency],
    value: currency,
  }))
  .sort((a, b) => a.text.localeCompare(b.text));

interface RedoCurrencySelectorProps {
  "data-dd-action-name"?: string;
  value: string;
  setValue: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: RedoDropdownInputSize;
  className?: string;
}

export const RedoCurrencySelector = memo(function RedoCurrencySelector({
  value,
  setValue,
  label,
  placeholder,
  disabled = false,
  size = "xs",
  className,
  "data-dd-action-name": actionName,
}: RedoCurrencySelectorProps) {
  const { t } = useTranslation();
  const [currencySearchString, setCurrencySearchString] = useState("");

  const filteredCurrencies = useMemo(
    () =>
      !currencySearchString
        ? CURRENCY_OPTIONS
        : CURRENCY_OPTIONS.filter(
            (option) =>
              option.value
                .toLowerCase()
                .includes(currencySearchString.toLowerCase()) ||
              option.text
                .toLowerCase()
                .includes(currencySearchString.toLowerCase()),
          ),
    [currencySearchString],
  );

  return (
    <RedoSingleSelectDropdownInput<Currency>
      className={className}
      data-arbiter="RedoCurrencySelector"
      data-dd-action-name={actionName}
      disabled={disabled}
      label={label ?? t("Currency", { context: "currency selector" })}
      options={filteredCurrencies}
      optionSelected={({ item }) => {
        setValue(item?.value || Currency.USD);
      }}
      placeholder={
        placeholder ?? t("Select currency", { context: "currency selector" })
      }
      searchPlaceholder={t("Search currencies...", {
        context: "currency selector",
      })}
      searchString={currencySearchString}
      selectedItem={CURRENCY_OPTIONS.find(
        (currency) => currency.value === value,
      )}
      setSearchString={setCurrencySearchString}
      size={size}
    />
  );
});
